import { useEffect, useRef, useState } from "react";

/**
 * Cloudflare Turnstile challenge (LimePhone).
 *
 * ⚠ Separate sitekey from LimeCall. The two brands have their own Cloudflare
 * widgets, and a token only verifies against the secret belonging to the
 * sitekey that minted it — so this reads VITE_TURNSTILE_SITE_KEY_LIMEPHONE and
 * must never fall back to LimeCall's.
 *
 * Renders nothing at all when the key is unset, so the form behaves exactly as
 * it does today until the key exists.
 *
 * ⚠⚠ Shipping this component is only HALF of turning the challenge on. The
 * server does not enforce until the product is named in
 * TURNSTILE_ENFORCE_PRODUCTS, and that ordering is deliberate: arming the
 * backend first is what took signups down for twelve hours on 2026-08-16, when
 * the secrets were set while no frontend rendered a widget and every
 * submission arrived tokenless. Ship this, confirm the sitekey is in the
 * SERVED bundle, then arm.
 *
 * The script is loaded on demand rather than in index.html: it is third-party
 * JS on the auth page, and there is no reason to fetch it for the visits that
 * never reach signup.
 */

const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY_LIMEPHONE as string | undefined;
const SCRIPT_ID = "cf-turnstile-script";
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      reset: (id?: string) => void;
      remove: (id?: string) => void;
    };
  }
}

/** True when a challenge will actually be shown — lets a form decide whether to
 *  wait for a token or submit straight away. */
export const turnstileEnabled = !!SITE_KEY;

function loadScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("turnstile script failed")));
    });
  }
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.id = SCRIPT_ID;
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("turnstile script failed"));
    document.head.appendChild(s);
  });
}

export function Turnstile({
  onToken,
  onError,
  resetKey = 0,
}: {
  onToken: (token: string | null) => void;
  onError?: () => void;
  /** Bump to get a fresh token. A token is single-use: after a refused
   *  submission the server has already spent it, so a retry needs a new one. */
  resetKey?: number;
}) {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const widgetRef = useRef<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!SITE_KEY || !boxRef.current) return;
    let cancelled = false;

    loadScript()
      .then(() => {
        if (cancelled || !boxRef.current || !window.turnstile) return;
        widgetRef.current = window.turnstile.render(boxRef.current, {
          sitekey: SITE_KEY,
          callback: (token: string) => onToken(token),
          // A solved challenge expires. Clearing the token forces a re-solve
          // rather than letting the form submit one the server will reject.
          "expired-callback": () => onToken(null),
          "error-callback": () => { onToken(null); setFailed(true); onError?.(); },
          theme: "auto",
          // Invisible unless Cloudflare actually needs the visitor to click;
          // it still runs and still mints the token the server requires.
          appearance: "interaction-only",
        });
      })
      .catch(() => {
        // Cloudflare unreachable, or an extension blocked the script. The
        // server allows a verify ERROR but refuses a MISSING TOKEN, and a
        // widget that never rendered produces the latter — so say what happened
        // and how to fix it rather than waving them through into a refusal they
        // cannot interpret.
        if (!cancelled) { setFailed(true); onError?.(); }
      });

    return () => {
      cancelled = true;
      try { if (widgetRef.current && window.turnstile) window.turnstile.remove(widgetRef.current); } catch { /* already gone */ }
    };
    // Mount once: re-rendering the widget would discard a solved token.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!resetKey || !widgetRef.current || !window.turnstile) return;
    onToken(null);
    try { window.turnstile.reset(widgetRef.current); } catch { /* widget gone */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  if (!SITE_KEY) return null;

  return (
    <div className={failed ? "mb-2" : undefined}>
      <div ref={boxRef} />
      {failed && (
        <p className="text-xs text-slate-500 mt-1.5">
          The verification check couldn't load — usually an ad blocker or privacy
          extension. Allow <b>challenges.cloudflare.com</b> and refresh.
        </p>
      )}
    </div>
  );
}
